import React from 'react';
import { toast } from 'react-hot-toast';
import { parseISO, format } from 'date-fns';
import { usePlanningMode } from '../hooks/usePlanningMode';
import { useMonEspacePlanning } from '../hooks/useMonEspacePlanning';
import { usePrivateAppointments } from '../hooks/usePrivateAppointments';
import { useMyValidatedWeeks, useValidateWeek, useUnvalidateWeek, useMyLeaveConfig, useHolidays } from '../hooks/useLeave';
import { workingDowSet } from '../components/LeaveCalendar/expandRequest';
import { AvailabilityCalendar } from '@/features/expert/tabs/preferences/components/AvailabilitySection/AvailabilityCalendar';
import { PrivateAppointment } from '../types/monEspace.types';
import { WeekSelectorCard } from '../components/PlanningRedesign/WeekSelectorCard';
import { formatHoursAsHM } from '../utils/time';
import { RecurringToggleCard } from '../components/PlanningRedesign/RecurringToggleCard';
import { SpecialDaysBanner } from '../components/PlanningRedesign/SpecialDaysBanner';
import { CheckCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import '../components/PlanningRedesign/PlanningRedesign.css';

// --- Week validation bar --------------------------------------------------
// Shown under the WeekSelector when the user is viewing a past week. Lets them
// "lock in" the encoded hours so the week starts contributing to the recovery
// counter. Future / current weeks are never validated.

const WeekValidationBar: React.FC<{ weekStart: string }> = ({ weekStart }) => {
  const year = parseInt(weekStart.slice(0, 4), 10);
  const { data: validated = [] } = useMyValidatedWeeks(year);
  const validateMut = useValidateWeek();
  const unvalidateMut = useUnvalidateWeek();
  // The user must explicitly certify the hours before validating. Reset the
  // certification whenever we switch to a different week.
  const [certified, setCertified] = React.useState(false);
  React.useEffect(() => { setCertified(false); }, [weekStart]);

  // Only show for past weeks (current week + future are not validatable)
  const todayIso = new Date().toISOString().slice(0, 10);
  const weekEnd = (() => {
    const [y, m, d] = weekStart.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 6);
    return dt.toISOString().slice(0, 10);
  })();
  const isPast = weekEnd < todayIso;
  if (!isPast) return null;

  const match = validated.find(v => v.week_start === weekStart);

  if (match) {
    const hours = parseFloat(match.planned_hours);
    return (
      <div className="mon-espace-validation-bar mon-espace-validation-bar--done">
        <CheckCircleIcon style={{ width: 18, height: 18, color: '#065f46' }} />
        <span>Semaine validée · <strong>{formatHoursAsHM(hours)}</strong> verrouillées dans le compteur récup.</span>
        <button
          className="mon-espace-validation-undo"
          onClick={() => {
            if (confirm("Dévalider cette semaine ? Elle ne comptera plus dans le compteur récup tant qu'elle n'est pas re-validée.")) {
              unvalidateMut.mutate(weekStart, {
                onSuccess: () => toast.success('Semaine dévalidée'),
                onError: () => toast.error('Erreur'),
              });
            }
          }}
        >
          Dévalider
        </button>
      </div>
    );
  }

  return (
    <div className="mon-espace-validation-bar">
      <div className="mon-espace-validation-bar-text">
        <span>⏰ Cette semaine est terminée. Vérifiez vos heures ci-dessous — vous pouvez encore les corriger — puis certifiez-les pour les ajouter au compteur récup.</span>
        <label className="mon-espace-validation-certify">
          <input
            type="checkbox"
            checked={certified}
            onChange={e => setCertified(e.target.checked)}
          />
          <span>Je certifie que ces heures sont correctes</span>
        </label>
      </div>
      <button
        className="mon-espace-validation-cta"
        onClick={() => {
          validateMut.mutate(weekStart, {
            onSuccess: () => toast.success('Semaine validée'),
            onError: () => toast.error("Erreur lors de la validation"),
          });
        }}
        disabled={!certified || validateMut.isPending}
      >
        <CheckCircleIcon style={{ width: 16, height: 16 }} />
        Valider cette semaine
      </button>
    </div>
  );
};

export const PlanningTab: React.FC<{ initialWeek?: string | null }> = ({ initialWeek }) => {
  const {
    mode,
    switchMode,
    currentWeekStart,
    goToPreviousWeek,
    goToNextWeek,
    goToCurrentWeek,
    isCurrentWeek,
    getWeekRangeString
  } = usePlanningMode(initialWeek);

  // A week is locked for editing once it has been *validated* (hours snapshotted
  // into the recup counter), NOT merely because it is in the past. This lets the
  // user still correct a finished-but-unvalidated week before validating it.
  const validatedYear = currentWeekStart
    ? parseInt(currentWeekStart.slice(0, 4), 10)
    : new Date().getFullYear();
  const { data: validatedWeeks = [] } = useMyValidatedWeeks(validatedYear);
  // Weekly contractual base — shown as the reference next to the worked-hours
  // badge ("33h00 / 38h00"). pg numeric comes back as a string, hence Number().
  const { data: myConfig } = useMyLeaveConfig(validatedYear);
  const expectedMinutes = myConfig?.base_hours_per_week != null
    ? Math.round(Number(myConfig.base_hours_per_week) * 60)
    : undefined;
  // Fériés only auto-credit the daily base for employees (paid day off).
  // Independents get no holiday pay — a férié is a day like any other.
  const isEmployee = myConfig?.employment_type === 'employee';

  // Public holidays of the displayed week — fériés on a contracted working
  // day auto-credit the daily base in the worked-hours total, mirroring
  // LeaveService.computeWeekPlannedHours.
  const weekEndIso = (() => {
    if (!currentWeekStart) return undefined;
    const [y, m, d] = currentWeekStart.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 6);
    return dt.toISOString().slice(0, 10);
  })();
  const { data: weekHolidays = [] } = useHolidays(
    currentWeekStart && weekEndIso ? { from: currentWeekStart, to: weekEndIso } : {}
  );
  const isWeekValidated =
    mode === 'week' &&
    !!currentWeekStart &&
    validatedWeeks.some(v => v.week_start === currentWeekStart);

  const {
    planning,
    hasOverrides,
    isLoading,
    isSaving,
    isDeleting,
    save,
    deleteWeekOverride
  } = useMonEspacePlanning({ mode, currentWeekStart, isLocked: isWeekValidated });

  // Fetch ALL appointments (no date filter)
  // This ensures recurring appointments are always visible in all weeks
  const {
    appointments,
    isLoading: isLoadingAppointments,
    createAppointment,
    updateAppointment,
    deleteAppointment
  } = usePrivateAppointments();

  const handlePlanningChange = (newPlanning: any) => {
    // This is now only used for local UI updates
    // Actual saving happens via handleAutoSave
  };

  const handleAutoSave = async (newPlanning: any) => {
    try {
      await save(newPlanning);
    } catch (error) {
      console.error('Error auto-saving planning:', error);
      throw error; // Re-throw so AvailabilityCalendar can handle it
    }
  };

  const handleDeleteOverride = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer la surcharge de cette semaine ?')) {
      try {
        await deleteWeekOverride?.();
      } catch (error) {
        console.error('Error deleting override:', error);
      }
    }
  };

  const handleModeChange = (newMode: typeof mode) => {
    switchMode(newMode);
  };

  // Handle appointment changes from AvailabilityCalendar
  const handleAppointmentsChange = async (updatedAppointments: PrivateAppointment[]) => {
    // Backstop for the validated-week lock: the calendar disables appointment
    // interactions when locked, but nothing else guards these API mutations.
    if (isWeekValidated) {
      toast.error('Cette semaine est validée — dévalidez-la pour la modifier');
      return;
    }
    try {
      // Find differences between current and updated appointments
      const currentIds = new Set(appointments.map(apt => apt.id));
      const updatedIds = new Set(updatedAppointments.map(apt => apt.id));

      // Find new appointments (no ID or ID not in current list)
      const newAppointments = updatedAppointments.filter(
        apt => !apt.id || !currentIds.has(apt.id)
      );

      // Find deleted appointments
      const deletedIds = Array.from(currentIds).filter(id => !updatedIds.has(id));

      // Find updated appointments (same ID but different data)
      const updatedExisting = updatedAppointments.filter(
        apt => apt.id && currentIds.has(apt.id) &&
        appointments.find(curr => curr.id === apt.id && JSON.stringify(curr) !== JSON.stringify(apt))
      );

      // Execute mutations
      for (const apt of newAppointments) {
        await createAppointment(apt);
      }

      for (const id of deletedIds) {
        await deleteAppointment(id);
      }

      for (const apt of updatedExisting) {
        const original = appointments.find(curr => curr.id === apt.id);

        // In week mode, recurring appointments cannot be edited directly
        // EXCEPT for adding exceptions (which is how we "delete" for a specific week)
        if (mode === 'week' && original?.recurring && !original.overrideParentId) {
          // Check if the only change is to the exceptions array
          const isOnlyExceptionChange =
            apt.date === original.date &&
            apt.dayOfWeek === original.dayOfWeek &&
            apt.startTime === original.startTime &&
            apt.endTime === original.endTime &&
            apt.title === original.title &&
            apt.travelTimeBefore === original.travelTimeBefore &&
            apt.travelTimeAfter === original.travelTimeAfter &&
            JSON.stringify(apt.startAddress) === JSON.stringify(original.startAddress) &&
            JSON.stringify(apt.endAddress) === JSON.stringify(original.endAddress) &&
            JSON.stringify(apt.exceptions) !== JSON.stringify(original.exceptions);

          if (!isOnlyExceptionChange) {
            console.warn('Cannot edit recurring appointments in week mode. Delete and create new instead.');
            continue; // Skip this update
          }
          // If it's only an exception change, allow the update to proceed
        }

        // Update non-recurring appointments or overrides normally
        await updateAppointment(apt.id, apt);
      }
    } catch (error) {
      console.error('Error updating appointments:', error);
    }
  };

  if (isLoading || isLoadingAppointments) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', flexDirection: 'column', gap: '16px' }}>
        <div className="mon-espace-spinner"></div>
        <p style={{ margin: 0, fontSize: '16px', color: '#6b7280' }}>Chargement de votre planning...</p>
      </div>
    );
  }

  // Total planned hours this week (sum of the 7 day slots from the current
  // planning state, MINUS encoded personal breaks). Personal breaks are
  // PrivateAppointments where isProfessional !== true; professional ones
  // (client visits, formations) are NOT deducted — they count as work.
  // Drives the "Xh travaillées" badge in the week selector. Returned as exact
  // minutes (not decimal hours) so the badge can render real h:mm — users read
  // a decimal like 38.3h as "38h30" and expect −30min to give 38h, hence the
  // "37h80" confusion. Keeping minutes also avoids the 0.1h rounding drift.
  const totalWorkedMinutes = (() => {
    const days = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'] as const;
    const toMinutes = (t: string) => {
      const [h, m] = t.split(':').map(Number);
      return (h || 0) * 60 + (m || 0);
    };
    // Biweekly breaks only land on even week offsets from their anchor date.
    // Mirrors the backend (LeaveService.biweeklyOccurs) and the calendar render
    // so the "Xh travaillées" badge matches what actually gets validated.
    // Dates are parsed as UTC midnights (NOT parseISO, which gives local
    // midnight) so the week count stays exact across DST transitions.
    const biweeklyOccurs = (anchorIso: string, targetIso: string) => {
      const a = new Date(anchorIso + 'T00:00:00Z').getTime();
      const t = new Date(targetIso + 'T00:00:00Z').getTime();
      const weeks = Math.floor((t - a) / (7 * 24 * 60 * 60 * 1000));
      return weeks >= 0 && weeks % 2 === 0;
    };
    // Date string for each day of the week being displayed. In 'recurrent'
    // mode there's no specific date, so we only match recurring appointments
    // by dayOfWeek; in 'week' mode we match both one-time (by date) and
    // recurring (by dayOfWeek, respecting exceptions / end date).
    const weekDates: Record<string, string | null> = {} as any;
    if (currentWeekStart) {
      const monday = parseISO(currentWeekStart);
      days.forEach((d, idx) => {
        const date = new Date(monday);
        date.setDate(monday.getDate() + idx);
        weekDates[d] = format(date, 'yyyy-MM-dd');
      });
    } else {
      days.forEach((d) => (weekDates[d] = null));
    }

    // Férié auto-credit (mirrors LeaveService.computeWeekPlannedHours),
    // EMPLOYEES only: a public holiday on a contracted working day credits
    // the daily base, and hours explicitly encoded on it (really worked)
    // count ON TOP — working a paid day off accrues récup. Independents get
    // no credit — their encoded hours count as-is.
    const holidaySet = new Set(weekHolidays.map(h => h.date));
    const workSet = workingDowSet(myConfig?.working_days);
    const dailyBaseMinutes = expectedMinutes ? expectedMinutes / (workSet.size || 5) : 0;

    let totalMinutes = 0;
    days.forEach((d, dayIdx) => {
      const slot = (planning as any)?.[d]?.slots?.[0];
      const dateForDay = weekDates[d];

      let dayMinutes = 0;
      const slotStart = slot?.enabled && slot.start ? toMinutes(slot.start) : 0;
      const slotEnd = slot?.enabled && slot.end ? toMinutes(slot.end) : 0;
      if (slotEnd > slotStart) {
        dayMinutes = slotEnd - slotStart;

        // Deduct overlapping non-professional appointments
        for (const apt of appointments || []) {
          if (apt.isProfessional === true) continue; // counts as work
          let matchesDay = mode === 'recurrent'
            ? !!apt.recurring && apt.dayOfWeek === d
            : (
                // One-time on this date
                apt.date === dateForDay
                // OR recurring matching this dayOfWeek, in range, not excepted
                || (
                  !!apt.recurring && apt.dayOfWeek === d && !!dateForDay
                  && apt.date <= dateForDay
                  && (!apt.recurrenceEndDate || apt.recurrenceEndDate >= dateForDay)
                  && !(apt.exceptions || []).includes(dateForDay)
                )
              );
          // Biweekly breaks only deduct on even week offsets (week mode only —
          // the recurrent template has no concrete date to anchor against).
          if (matchesDay && mode === 'week' && apt.recurring && apt.recurrencePattern === 'biweekly' && dateForDay) {
            matchesDay = biweeklyOccurs(apt.date, dateForDay);
          }
          if (!matchesDay) continue;
          const aStart = toMinutes(apt.startTime);
          const aEnd = toMinutes(apt.endTime);
          if (aEnd <= aStart) continue;
          // Overlap with the work slot
          const overlap = Math.max(0, Math.min(slotEnd, aEnd) - Math.max(slotStart, aStart));
          dayMinutes -= overlap;
        }
        dayMinutes = Math.max(0, dayMinutes);
      }

      if (isEmployee && mode === 'week' && dateForDay && holidaySet.has(dateForDay) && workSet.has(dayIdx)) {
        dayMinutes += dailyBaseMinutes;
      }

      totalMinutes += dayMinutes;
    });
    return Math.round(totalMinutes);
  })();

  return (
    <div className="pl-rd">
      {/* Top row: week selector + recurring toggle */}
      <div className="pl-rd-top-row">
        <WeekSelectorCard
          weekRangeString={getWeekRangeString()}
          minutesTotal={totalWorkedMinutes}
          expectedMinutes={expectedMinutes}
          isCurrentWeek={isCurrentWeek()}
          onPrevious={goToPreviousWeek}
          onNext={goToNextWeek}
          onToday={goToCurrentWeek}
        />
        <RecurringToggleCard
          mode={mode}
          onModeChange={handleModeChange}
          disabled={isSaving || isDeleting}
        />
      </div>

      {/* Special days banner — fériés + congés approuvés cette semaine */}
      {mode === 'week' && currentWeekStart && (
        <SpecialDaysBanner weekStart={currentWeekStart} isEmployee={isEmployee} />
      )}

      {/* Past-week + validation bars */}
      <div>
        {isWeekValidated && (
          <div className="mon-espace-past-week-notice" style={{ marginBottom: 12 }}>
            🔒 Cette semaine est validée et verrouillée. Dévalidez-la pour la modifier.
          </div>
        )}
        {mode === 'week' && currentWeekStart && (
          <WeekValidationBar weekStart={currentWeekStart} />
        )}
      </div>

      {/* The day-cards row + timeline calendar are rendered inside
          AvailabilityCalendar below. CSS overrides in PlanningRedesign.css
          restyle those existing pieces to match the mock — no duplication. */}

      {/* Detailed calendar wrapped in a card */}
      <div className="pl-rd-detail">
        <div className="pl-rd-detail-header">
          <h3 className="pl-rd-section-title">Planning détaillé</h3>
          <div className="pl-rd-detail-actions">
            <button className="pl-rd-action-btn" title="Bientôt — changer de vue (semaine / mois)" disabled>
              <span>Vue semaine</span>
            </button>
            <button className="pl-rd-action-btn" title="Bientôt — exporter le planning" disabled>
              <ArrowDownTrayIcon />
              Exporter
            </button>
          </div>
        </div>
        <AvailabilityCalendar
          availability={planning}
          domicileAddresses={[]}
          onAvailabilityChange={handlePlanningChange}
          onAutoSave={handleAutoSave}
          privateAppointments={appointments}
          onPrivateAppointmentsChange={handleAppointmentsChange}
          mode={mode}
          currentWeekStart={currentWeekStart}
          isPastWeek={isWeekValidated}
          isEmployee={isEmployee}
        />
      </div>

      {/* Autosave confirmation pill */}
      <div className="pl-rd-autosave">
        <span className="pl-rd-autosave-pill">
          <CheckCircleIcon />
          Toutes les modifications sont enregistrées automatiquement.
        </span>
      </div>
    </div>
  );
};
